// Mirrored from mju-ai-workbench/shared/schemas/purviewApply.ts — keep the two identical but for this line and the import extensions.
/**
 * Protected (encrypting) sensitivity labels applied through the organisation
 * (phase 4 of docs/plans/2026-10-purview-sensitivity-labels.md, section 6.5;
 * the contract is docs/plans/2026-10-agent-365-aioe-contract.md, "Purview
 * labels", P4-§1 to P4-§4).
 *
 * A label whose protection encrypts the file cannot be written on the
 * machine without the MIP SDK. When the organisation offers it (the policy's
 * `microsoft365.protectedLabels.sharepoint`), a host hands the file to the
 * AIOE, which uploads it to the organisation's SharePoint / OneDrive location,
 * calls Graph's `driveItem: assignSensitivityLabel` (v1.0, long-running) and
 * hands the protected file back:
 *
 *   POST /m365/v1/purview/apply-label                  → 202 { jobId, status }
 *   GET  /m365/v1/purview/apply-label/{jobId}          → the job, polled
 *   GET  /m365/v1/purview/apply-label/{jobId}/content  → the protected bytes
 *
 * The device's own AIOE token is the bearer on all three. The AIOE is the only
 * Graph caller; nothing here names a tenant, a site or a drive.
 *
 * No runtime dependency besides zod.
 */
import { z } from "zod";
/** The largest file a host sends (before base64). The AIOE may refuse smaller ones with `too_large`. */
export const PURVIEW_APPLY_MAX_BYTES = 25 * 1024 * 1024;
/** What `assignSensitivityLabel` labels (Office files and PDF); everything else is refused before it is sent. */
export const PURVIEW_APPLY_EXTENSIONS = ["docx", "docm", "xlsx", "xlsm", "pptx", "pptm", "pdf"];
const guid = z.string().regex(/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/);
const sha256 = z.string().regex(/^[0-9a-f]{64}$/);
/** `POST /m365/v1/purview/apply-label`. */
export const purviewApplyRequestSchema = z.object({
    /** The file's base name: the AIOE keeps the extension, which decides how SharePoint protects it. */
    fileName: z.string().min(1).max(255),
    /** The file's bytes, base64. */
    contentBase64: z.string().min(1),
    /** SHA-256 of the bytes, lower-case hex: the AIOE refuses a body that does not match. */
    sha256,
    /** The label to assign: a GUID from the taxonomy the AIOE published. */
    labelId: guid,
    /** `justificationText` on the Graph call (audit; Graph requires it when the item already carries a higher label). */
    justification: z.string().min(1).max(1000),
    /** `assignmentMethod` on the Graph call. The workbench applies a session's mark: `standard`. */
    assignmentMethod: z.enum(["standard", "privileged", "auto"]).default("standard"),
    /** What produced it, for the AIOE's own audit and to pick the project's location. */
    sessionId: z.string().min(1).max(200).optional(),
    projectId: z.string().min(1).max(200).optional(),
    rootId: z.string().min(1).max(200).optional(),
});
/**
 * The error codes a job (or the POST itself, as `{ error }` with a 4xx) carries:
 *
 *  - `not_enabled` — the organisation has not turned the route on;
 *  - `consent_required` — the tenant has not consented to `Files.ReadWrite.All`;
 *  - `billing_required` — `assignSensitivityLabel` is a metered, protected Graph API and the
 *    tenant has no billing set up for it (or Microsoft has not approved the app for it);
 *  - `no_location` — no SharePoint / OneDrive location for this project or person;
 *  - `label_not_found` / `label_not_appliable` — the label is unknown, or not published to the person;
 *  - `unsupported_type` — SharePoint does not label this kind of file;
 *  - `too_large` — over {@link PURVIEW_APPLY_MAX_BYTES} or the AIOE's own limit;
 *  - `delegated_required` — Graph answered that this protected label cannot be applied app-only;
 *  - `locked` — Graph answered 423 (double key encryption, decryption deferred or not supported);
 *  - `failed` — anything else; `message` says what.
 */
export const PURVIEW_APPLY_ERRORS = ["not_enabled", "consent_required", "billing_required", "no_location", "label_not_found", "label_not_appliable", "unsupported_type", "too_large", "delegated_required", "locked", "failed"];
/** The answer to the POST (202) and to each poll (200). */
export const purviewApplyJobSchema = z.object({
    jobId: z.string().min(1).max(200),
    status: z.enum(["queued", "running", "succeeded", "failed"]),
    /** When to poll again; the host clamps it to 1–30 s. */
    pollAfterMs: z.number().int().min(0).optional(),
    /** Set on `failed`: one of {@link PURVIEW_APPLY_ERRORS} (an unknown code reads as `failed`). */
    error: z.string().max(100).optional(),
    /** Words for a person, set on `failed`. */
    message: z.string().max(1000).optional(),
    /** Set on `succeeded`: the label the item carries now, as Graph reports it. */
    labelId: guid.optional(),
    /** Set on `succeeded`: SHA-256 and size of the protected bytes `…/content` serves. */
    sha256: sha256.optional(),
    size: z.number().int().min(0).optional(),
    /** Set on `succeeded`: the labelled item in SharePoint / OneDrive, when the AIOE keeps it. */
    webUrl: z.string().url().optional(),
});
//# sourceMappingURL=purviewApply.js.map